import { useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiSearch,
  FiDownload,
  FiEye,
  FiPrinter,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const statusLabels = {
  DRAFT: "Draft",
  SUBMITTED: "Pending Approval",
  APPROVED: "Approved",
  PARTIALLY_RECEIVED: "Partially Received",
  RECEIVED: "Received",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
};

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadOrders() {
      try {
        setIsLoading(true);
        setError("");

        const params = new URLSearchParams();

        if (search.trim()) params.set("search", search.trim());
        if (status) params.set("status", status);

        const query = params.toString();
        const response = await apiRequest(
          `/purchases/orders${query ? `?${query}` : ""}`,
          { accessToken },
        );

        setOrders(Array.isArray(response) ? response : []);
      } catch (requestError) {
        setError(
          requestError.message || "Could not load Purchase Orders.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    const timer = setTimeout(() => {
      if (accessToken) loadOrders();
    }, 250);

    return () => clearTimeout(timer);
  }, [accessToken, search, status]);

  const filteredOrders = useMemo(() => {
    if (!selectedDate) return orders;

    return orders.filter((order) => {
      const orderDate = new Date(order.orderDate || order.createdAt)
        .toISOString()
        .slice(0, 10);

      return orderDate === selectedDate;
    });
  }, [orders, selectedDate]);

  const stats = useMemo(() => {
    const totalValue = filteredOrders.reduce(
      (sum, order) => sum + Number(order.totalAmount || 0),
      0,
    );

    return {
      total: filteredOrders.length,
      pending: filteredOrders.filter(
        (order) => order.status === "SUBMITTED",
      ).length,
      approved: filteredOrders.filter(
        (order) =>
          order.status === "APPROVED" ||
          order.status === "PARTIALLY_RECEIVED",
      ).length,
      totalValue,
    };
  }, [filteredOrders]);

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (value) => {
    if (!value) return "—";

    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const exportCsv = () => {
    const rows = [
      ["PO Number", "Supplier", "Store", "Date", "Items", "Amount", "Status"],
      ...filteredOrders.map((order) => [
        order.purchaseOrderNumber,
        order.supplier?.displayName || "",
        order.store?.name || "",
        formatDate(order.orderDate || order.createdAt),
        order._count?.items || 0,
        Number(order.totalAmount || 0),
        statusLabels[order.status] || order.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = "purchase-orders.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Purchasing / Purchase Orders</p>
          <h1 className="mt-1 text-3xl font-bold">Purchase Orders</h1>
          <p className="mt-2 text-gray-500">
            Manage supplier purchase orders and deliveries.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={exportCsv}
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={() => navigate("/admindashboard/purchases/create")}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
          >
            <FiPlus />
            Create PO
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard title="Total POs" value={stats.total} icon={FiTruck} color="blue" />
        <StatCard title="Pending Approval" value={stats.pending} icon={FiClock} color="orange" />
        <StatCard title="Approved" value={stats.approved} icon={FiCheckCircle} color="green" />
        <StatCard title="PO Value" value={formatCurrency(stats.totalValue)} icon={FiTruck} color="purple" />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search PO / Supplier..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Status</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">PO Number</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Supplier</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Date</th>
                <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">Items</th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">Amount</th>
                <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">Status</th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!isLoading && filteredOrders.map((order) => (
                <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">{order.purchaseOrderNumber}</td>
                  <td className="px-6 py-4">
                    <p>{order.supplier?.displayName || "—"}</p>
                    <p className="mt-1 text-xs text-gray-400">{order.store?.name || ""}</p>
                  </td>
                  <td className="px-6 py-4">{formatDate(order.orderDate || order.createdAt)}</td>
                  <td className="px-6 py-4 text-center">{order._count?.items || 0}</td>
                  <td className="px-6 py-4 text-right font-semibold">{formatCurrency(order.totalAmount)}</td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admindashboard/purchases/${order.id}`)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 transition-all hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                        title="View PO"
                      >
                        <FiEye />
                      </button>

                      <button
                        type="button"
                        onClick={() => window.print()}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200"
                        title="Print"
                      >
                        <FiPrinter />
                      </button>

                      {(order.status === "APPROVED" ||
                        order.status === "PARTIALLY_RECEIVED") && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/admindashboard/purchases/grn/create?purchaseOrderId=${order.id}`,
                            )
                          }
                          className="h-9 rounded-lg bg-primary-700 px-3 text-xs font-medium text-white"
                        >
                          GRN
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {!isLoading && filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-sm text-gray-500">
                    No Purchase Orders found.
                  </td>
                </tr>
              )}

              {isLoading && (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading Purchase Orders...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between p-5">
          <p className="text-sm text-gray-500">
            Showing {filteredOrders.length} Purchase Orders
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    SUBMITTED: "bg-orange-100 text-orange-700",
    APPROVED: "bg-blue-100 text-blue-700",
    PARTIALLY_RECEIVED: "bg-purple-100 text-purple-700",
    RECEIVED: "bg-green-100 text-green-700",
    CLOSED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {statusLabels[status] || status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{value}</h3>
        </div>

        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${colors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}